import { inject, Injectable } from '@angular/core';
import type {
  SrmCategoryTreeModel,
  SrmFileModel,
  SrmTicketCreateModel,
  SrmTicketListModel,
  SrmTicketModel,
  SrmTicketSuggestionModel,
  SrmTicketSuggestionRequestModel,
} from '@libs/services/models';
import { FileUploadModel, SingleStepFormItemModel } from '@libs/form/models';
import { ConnectionParams } from '@libs/shared/models';
import { Observable } from 'rxjs';
import { ConnectorService } from '@libs/shared/services';

@Injectable({ providedIn: 'root' })
export class SrmRepositoryService {
  private readonly connector: ConnectorService = inject(ConnectorService);

  public getCategoryTree(location?: string): Observable<SrmCategoryTreeModel[]> {
    const params: ConnectionParams = location ? { searchParams: { location } } : {};

    return this.connector.connect<SrmCategoryTreeModel[]>(
      'service',
      'srmCategoryTree',
      params,
    );
  }

  public getForm(formIdentifier: string): Observable<SingleStepFormItemModel> {
    return this.connector.connect<SingleStepFormItemModel>(
      'service',
      'srmForm',
      {
        pathParams: {
          formIdentifier,
        },
      },
    );
  }

  public createTicket(ticket: SrmTicketCreateModel): Observable<SrmTicketModel> {
    return this.connector.connect<SrmTicketModel>(
      'service',
      'srmTicketCreate',
      {
        payload: ticket,
      },
    );
  }

  public getTicket(ticketIdentifier: string): Observable<SrmTicketModel | undefined> {
    return this.connector.connect<SrmTicketModel | undefined>(
      'service',
      'srmTicket',
      {
        pathParams: {
          ticketIdentifier,
        },
      },
    );
  }

  public getTickets(): Observable<SrmTicketListModel | undefined> {
    return this.connector.connect<SrmTicketListModel | undefined>(
      'service',
      'srmTickets',
    );
  }

  public uploadFile(file: File): Observable<FileUploadModel> {
    const formData: FormData = new FormData();

    formData.append('file', file, file.name);

    return this.connector.connect<FileUploadModel>(
      'service',
      'srmFileUpload',
      {
        payload: formData,
      },
    );
  }

  public getFile(fileIdentifier: string): Observable<SrmFileModel> {
    return this.connector.connect<SrmFileModel>(
      'service',
      'srmFile',
      {
        pathParams: {
          fileIdentifier,
        },
      },
    );
  }

  public getTicketSuggestion(request: SrmTicketSuggestionRequestModel): Observable<SrmTicketSuggestionModel> {
    return this.connector.connect<SrmTicketSuggestionModel>(
      'service',
      'srmTicketSuggestion',
      {
        payload: request,
      },
    );
  }

  public getTicketSuggestionFile(fileIdentifier: string): Observable<SrmFileModel> {
    return this.connector.connect<SrmFileModel>(
      'service',
      'srmTicketSuggestionFile',
      {
        pathParams: {
          fileIdentifier,
        },
      },
    );
  }
}
